import {
  ButtonDisplayMode,
  ButtonHierarchy,
  ButtonShape,
  ButtonSize,
  MainButton,
  Typography,
  TypographySize,
  TypographyWeight,
} from "@beydesign/storybook"
import { Box } from "@mui/material"
import React, { useEffect, useMemo, useState } from "react"
import CheckIcon from "../../assets/check.svg"
import { useStripeProducts } from "../../hooks"
import { PlanUpdateStatus } from "../../types"

interface PlanUpdateModalProps {}

const statuses: PlanUpdateStatus[] = ["upgraded", "downgraded", "changed"]

export default function PlanUpdateModal({}: PlanUpdateModalProps) {
  const [isVisible, setIsVisible] = useState(false)

  const urlParams = new URLSearchParams(window.location.search)
  const status = (urlParams.get("status") as PlanUpdateStatus) || null
  const sessionId = urlParams.get("session_id") || null
  const isSessionId = sessionId?.trim().length > 0

  const { refetch } = useStripeProducts()

  useEffect(() => {
    const handlePlanUpdate = async () => {
      if (!status && !sessionId) return
      else if (statuses.includes(status) || isSessionId) {
        setIsVisible(true)
        await refetch()
      }
    }
    handlePlanUpdate()
  }, [status, isSessionId])

  const handleClose = () => {
    setIsVisible(false)
  }

  const text = useMemo(() => {
    if (status === "upgraded")
      return "Thank you for upgrading! Your subscription has been updated, and the changes take effect immediately."
    if (status === "downgraded")
      return "Your subscription has been downgraded. Some features or usage limits from your previous plan will no longer be available."
    if (status === "changed" || isSessionId)
      return "Your subscription has been updated, and the changes take effect immediately."
    return ""
  }, [status, isSessionId])

  if (!isVisible) return null

  return (
    <Box
      display={"flex"}
      flexDirection={"column"}
      justifyContent={"center"}
      alignItems={"center"}
      position={"fixed"}
      top={0}
      left={0}
      zIndex={100}
      width={"100%"}
      height={"100%"}
      bgcolor={"var(--colors-light-background-bg-overlay-1)"}
    >
      <Box
        width={"420px"}
        display={"flex"}
        flexDirection={"column"}
        alignItems={"center"}
        position={"relative"}
        bgcolor={"var(--colors-light-background-bg-page)"}
        padding={"var(--spacing-tokens-size-in-px-spacing-spacing-3xl)"}
        borderRadius={"var(--spacing-tokens-size-in-px-spacing-spacing-lg)"}
        gap={"var(--spacing-tokens-size-in-px-spacing-spacing-2xl)"}
      >
        <Box sx={{ position: "absolute", top: "0.8rem", right: "0.8rem" }}>
          <MainButton
            onClick={handleClose}
            displayIconMode={ButtonDisplayMode.Only}
            displayIcon="X"
            shape={ButtonShape.Circle}
            hierarchy={ButtonHierarchy.Tertiary}
            size={ButtonSize.md}
            text="Close"
          />
        </Box>

        <img src={CheckIcon} alt="Check" width={"64px"} height={"64px"} />

        <Typography
          text={`Your plan has been successfully ${status || "changed"}!`}
          size={TypographySize.HeadingS}
          weight={TypographyWeight.SemiBold}
          className="text-center"
          color="var(--colors-light-text-text-title)"
        />

        <Typography
          text={text}
          size={TypographySize.TextS}
          weight={TypographyWeight.Regular}
          color="var(--colors-light-text-text-paragraph)"
          className="text-center"
        />

        <MainButton
          onClick={handleClose}
          style={{ marginTop: "0.5rem" }}
          size={ButtonSize.sm}
          text="Got it. Thanks!"
        />
      </Box>
    </Box>
  )
}
